<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
import AcoesPasso from '../components/AcoesPasso.vue';
import DicaDestaque from '../components/DicaDestaque.vue';
import FonteChip from '../components/FonteChip.vue';
import {
  BTN,
  BTN_SECONDARY,
  CARD,
  CHIP,
  CHIP_TONES,
  ENTER,
  FIELD,
  H1,
  SMALL,
  SUB,
  TILE,
  TILE_TONES,
} from '../ui';

const props = defineProps({
  state: { type: Object, required: true },
  properties: { type: Object, default: null },
});
const emit = defineEmits(['next', 'saveFacts']);

const { t } = useI18n();

const FIELDS = [
  { key: 'name', icon: 'i-ph-storefront-duotone' },
  { key: 'website', icon: 'i-ph-globe-duotone' },
  { key: 'city', icon: 'i-ph-map-pin-duotone' },
  { key: 'whatsapp', icon: 'i-ph-device-mobile-duotone' },
];

const facts = computed(() => props.state.facts || {});
const realEstate = computed(() => Boolean(props.state.real_estate));
const fromRadar = computed(() =>
  Object.values(facts.value).some(fact => fact?.source === 'radar')
);
const shortName = computed(
  () => (facts.value.name?.value || '').split(' ').slice(0, 2).join(' ') || ''
);
const neighborhoods = computed(() => facts.value.neighborhoods?.value || []);

const editing = ref(null);
const draft = ref('');
const newNeighborhood = ref('');
const inputs = ref({});

const startEdit = async key => {
  editing.value = key;
  draft.value = facts.value[key]?.value || '';
  await nextTick();
  inputs.value[key]?.focus();
};

const commitEdit = () => {
  const key = editing.value;
  if (!key) return;
  editing.value = null;
  const value = draft.value.trim();
  if (value && value !== facts.value[key]?.value) {
    emit('saveFacts', { [key]: value });
  }
};

const addNeighborhood = () => {
  const value = newNeighborhood.value.trim();
  newNeighborhood.value = '';
  if (!value || neighborhoods.value.includes(value)) return;
  emit('saveFacts', { neighborhoods: [...neighborhoods.value, value] });
};

const removeNeighborhood = name => {
  emit('saveFacts', {
    neighborhoods: neighborhoods.value.filter(item => item !== name),
  });
};

const photos = computed(() =>
  (props.properties?.items || [])
    .map(item => item.photo)
    .filter(Boolean)
    .slice(0, 4)
);

const tips = computed(() => [
  ...(realEstate.value ? [t('ONBOARDING_VISOR.WELCOME.TIPS.NEIGHBORHOODS')] : []),
  t('ONBOARDING_VISOR.WELCOME.TIPS.WHATSAPP'),
  t('ONBOARDING_VISOR.WELCOME.TIPS.NOT_PUBLISHED'),
]);
</script>

<template>
  <div>
    <section class="flex gap-6 items-center max-md:flex-col max-md:items-start max-md:gap-3" :class="ENTER">
      <BrasaMascot state="wave" :size="88" class="flex-none" />
      <div>
        <h1 :class="H1">
          <span class="text-n-slate-10">
            {{ t('ONBOARDING_VISOR.WELCOME.HELLO', { name: shortName }) }}
          </span>
          <br />
          {{
            fromRadar
              ? t('ONBOARDING_VISOR.WELCOME.TITLE_RADAR')
              : t('ONBOARDING_VISOR.WELCOME.TITLE')
          }}
        </h1>
        <p :class="SUB">
          {{
            fromRadar
              ? t('ONBOARDING_VISOR.WELCOME.SUBTITLE_RADAR')
              : t('ONBOARDING_VISOR.WELCOME.SUBTITLE')
          }}
        </p>
      </div>
    </section>

    <div class="grid gap-6 mt-8 lg:grid-cols-[1.55fr_1fr] items-start max-md:gap-4 max-md:mt-5">
      <section :class="CARD">
        <div class="flex items-center gap-3 mb-4">
          <span :class="[TILE, TILE_TONES.azul]">
            <Icon icon="i-ph-broadcast-duotone" />
          </span>
          <h2 class="text-xl font-medium tracking-[-0.02em]">
            {{ t('ONBOARDING_VISOR.WELCOME.FACTS_TITLE') }}
          </h2>
          <span class="ml-auto max-md:hidden" :class="SMALL">
            {{ t('ONBOARDING_VISOR.WELCOME.FACTS_HINT') }}
          </span>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div v-for="field in FIELDS" :key="field.key" :data-testid="`fato-${field.key}`">
            <div class="flex items-center gap-2 mb-1.5 text-[0.8125rem] text-n-slate-11">
              {{ t(`ONBOARDING_VISOR.WELCOME.FIELDS.${field.key.toUpperCase()}`) }}
              <FonteChip :source="facts[field.key]?.source" />
            </div>
            <label :class="FIELD" class="focus-within:border-n-blue-9">
              <Icon :icon="field.icon" class="size-5 flex-none text-n-slate-10" />
              <input
                v-if="editing === field.key"
                :ref="el => (inputs[field.key] = el)"
                v-model="draft"
                class="flex-1 min-w-0 bg-transparent !border-0 !m-0 !p-0 !h-auto !outline-none !shadow-none text-n-slate-12"
                @keydown.enter.prevent="commitEdit"
                @keydown.esc="editing = null"
                @blur="commitEdit"
              />
              <span v-else class="flex-1 min-w-0 truncate" :class="facts[field.key]?.value ? '' : 'text-n-slate-10'">
                {{ facts[field.key]?.value || t('ONBOARDING_VISOR.WELCOME.EMPTY') }}
              </span>
              <button
                v-if="editing !== field.key"
                type="button"
                class="text-n-slate-10 hover:text-n-slate-12"
                :aria-label="t('ONBOARDING_VISOR.WELCOME.EDIT')"
                @click="startEdit(field.key)"
              >
                <Icon icon="i-ph-pencil-simple-duotone" class="size-5" />
              </button>
            </label>
          </div>

          <div class="md:col-span-2" data-testid="fato-neighborhoods">
            <div class="flex items-center gap-2 mb-1.5 text-[0.8125rem] text-n-slate-11">
              {{ t('ONBOARDING_VISOR.WELCOME.FIELDS.NEIGHBORHOODS') }}
              <FonteChip :source="facts.neighborhoods?.source" />
            </div>
            <div :class="FIELD" class="flex-wrap !py-2.5 !gap-2">
              <span
                v-for="name in neighborhoods"
                :key="name"
                :class="[CHIP, CHIP_TONES.neutro]"
              >
                {{ name }}
                <button
                  type="button"
                  class="text-n-slate-10 hover:text-n-slate-12"
                  :aria-label="t('ONBOARDING_VISOR.WELCOME.REMOVE', { name })"
                  @click="removeNeighborhood(name)"
                >
                  <Icon icon="i-ph-x" class="size-3" />
                </button>
              </span>
              <span :class="[CHIP, CHIP_TONES.azul]" class="!pr-1">
                <Icon icon="i-ph-plus-circle-duotone" class="size-3.5" />
                <input
                  v-model="newNeighborhood"
                  class="w-32 bg-transparent !border-0 !m-0 !p-0 !h-auto !outline-none !shadow-none text-n-blue-11 placeholder:text-n-blue-11"
                  :placeholder="t('ONBOARDING_VISOR.WELCOME.ADD_NEIGHBORHOOD')"
                  @keydown.enter.prevent="addNeighborhood"
                  @blur="addNeighborhood"
                />
              </span>
            </div>
          </div>

          <div class="md:col-span-2" data-testid="fato-marca">
            <div class="flex items-center gap-2 mb-1.5 text-[0.8125rem] text-n-slate-11">
              {{ t('ONBOARDING_VISOR.WELCOME.FIELDS.BRAND') }}
              <FonteChip :source="facts.logo_url?.source || facts.brand_color?.source" />
            </div>
            <div :class="FIELD">
              <img
                v-if="facts.logo_url?.value"
                :src="facts.logo_url.value"
                alt=""
                class="size-8 rounded-lg object-contain bg-white"
              />
              <Icon v-else icon="i-ph-image-duotone" class="size-5 text-n-slate-10" />
              <span :class="facts.logo_url?.value ? '' : 'text-n-slate-10'">
                {{
                  facts.logo_url?.value
                    ? t('ONBOARDING_VISOR.WELCOME.LOGO_FOUND')
                    : t('ONBOARDING_VISOR.WELCOME.LOGO_MISSING')
                }}
              </span>
              <span v-if="facts.brand_color?.value" class="flex items-center gap-2 ml-3 text-sm text-n-slate-11">
                <i
                  class="block size-[1.125rem] rounded-md"
                  :style="{ background: facts.brand_color.value }"
                />
                {{ facts.brand_color.value }}
              </span>
            </div>
            <p class="mt-1.5" :class="SMALL">
              {{ t('ONBOARDING_VISOR.WELCOME.BRAND_HINT') }}
            </p>
          </div>
        </div>
      </section>

      <div class="grid gap-6 max-md:gap-4">
        <section v-if="realEstate" :class="CARD" data-testid="resumo-imoveis">
          <div class="flex items-center gap-3 mb-4">
            <span :class="[TILE, TILE_TONES.ok]">
              <Icon icon="i-ph-buildings-duotone" />
            </span>
            <h3 class="font-medium">
              {{
                properties?.total
                  ? t('ONBOARDING_VISOR.WELCOME.PROPERTIES_FOUND', { n: properties.total })
                  : t('ONBOARDING_VISOR.WELCOME.PROPERTIES_NONE')
              }}
            </h3>
          </div>
          <div v-if="photos.length" class="grid grid-cols-4 gap-2">
            <img
              v-for="photo in photos"
              :key="photo"
              :src="photo"
              alt=""
              class="aspect-square w-full rounded-lg object-cover"
            />
          </div>
          <p class="mt-3" :class="SMALL">
            {{
              properties?.total
                ? t('ONBOARDING_VISOR.WELCOME.PROPERTIES_HINT')
                : t('ONBOARDING_VISOR.WELCOME.PROPERTIES_CONNECT')
            }}
          </p>
        </section>
        <DicaDestaque :title="t('ONBOARDING_VISOR.WELCOME.TIPS_TITLE')" :tips="tips" />
      </div>
    </div>

    <AcoesPasso :trust="t('ONBOARDING_VISOR.WELCOME.TRUST')">
      <button type="button" :class="BTN" data-testid="seguir" @click="emit('next')">
        {{ t('ONBOARDING_VISOR.WELCOME.CONFIRM') }}
        <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
      </button>
      <button type="button" :class="BTN_SECONDARY" class="max-md:hidden" @click="startEdit('name')">
        {{ t('ONBOARDING_VISOR.WELCOME.FIX') }}
      </button>
    </AcoesPasso>
  </div>
</template>
